import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { SearchDataProps } from '@components/search-dialog';
import { AddIcon, CloseIcon, CreationIcon, LessonIcon, SearchIcon } from '@sketchnote/icons';
import { FileIcon } from '@sketchnote/icons';
import { LoaderXs } from '@sketchnote/shared-components';
import classNames from 'classnames';
import { Emoji } from 'emoji-mart';
import debounce from 'lodash/debounce';
import router from 'next/router';
import * as React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { API, endpoints } from 'src/api';
import { StartNewFile } from 'src/components/sidebar/components/start-new-file';
import { useFileStore } from 'src/features/files';

const { useState, useEffect, useRef, useCallback } = React;

const searchIcons = {
  lesson: LessonIcon,
  creation: CreationIcon,
};

const SearchInput = ({
  setSearchString,
  inputRef,
  nextRef,
}: {
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
  inputRef: React.RefObject<HTMLInputElement>;
  nextRef: React.RefObject<HTMLButtonElement>;
}) => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [onSearchFocus, setOnSearchFocus] = useState<boolean>(false);

  const onSearch = debounce((value) => {
    setSearchString(value);
  }, 300);

  const onEnterSearch = (e) => {
    setSearchKeyword(e.target.value);
    onSearch(e.target.value);
  };

  const onClearSearch = () => {
    setSearchKeyword('');
    setSearchString('');
  };

  const checkKeyPresses = (e) => {
    if (e.which === 13) {
      e.preventDefault();
      nextRef?.current?.focus();
    }
  };

  return (
    <div className=" bg-gray-100 w-full rounded-sm flex items-center py-8 px-12 text-gray-600 overflow-hidden mb-12">
      <div
        className="w-full flex relative"
        style={{
          transition: 'all 300ms',
          transform: onSearchFocus ? 'translateX(-26px)' : '',
        }}
      >
        <SearchIcon wrapperClass="text-gray-500 flex items-center mr-8" />
        <input
          type="text"
          className="w-full bg-gray-100 placeholder-gray-500 text-base"
          placeholder={onSearchFocus ? 'Search' : 'Search for a file...'}
          onChange={onEnterSearch}
          value={searchKeyword}
          ref={inputRef}
          autoFocus={true}
          onFocus={() => setOnSearchFocus(true)}
          onBlur={() => setOnSearchFocus(false)}
          onKeyPress={checkKeyPresses}
        />
      </div>
      {searchKeyword.length > 0 && (
        <CloseIcon
          className="items-end text-gray-500 cursor-pointer"
          size={10}
          onClick={onClearSearch}
        />
      )}
    </div>
  );
};

type SearchItemProps = {
  id?: string;
  title?: string;
  subTitle?: string;
  isShared?: boolean;
  icon?: any;
  tags?: any;
  isDone?: boolean;
  onSelect: (id: string, title: string, icon: any) => void;
  isActive?: boolean;
  itemRef: React.RefObject<HTMLButtonElement> | null;
};

const SearchItem = ({
  id = '',
  icon: Icon,
  title = '',
  subTitle = '',
  isShared = false,
  onSelect,
  isActive = true,
  itemRef,
}: SearchItemProps) => {
  const workspaceName =
    subTitle === 'default-workspace' ? (isShared ? 'Shared' : 'Private') : subTitle ?? 'Private';
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  return (
    <button
      className={classNames({
        'flex w-full py-8 px-8 search-dialog-result-item cursor-pointer': true,
        [`styles['search-dialog-result-item-active'] flex hover:bg-gray-100`]: isActive,
        'bg-gray-100': hasFocus,
      })}
      onClick={() => onSelect(id, title, Icon)}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      ref={itemRef}
    >
      <div className={classNames('mt-2 mr-10 w-16 h-16 items-center')}>
        {Icon?.id && Icon?.type !== 'image' ? (
          <Emoji emoji={Icon?.id} set="apple" size={16} />
        ) : Icon?.id && Icon?.type === 'image' ? (
          <img className=" object-center object-cover w-16 h-16" src={Icon?.id} />
        ) : (
          <span className="bg-gray-100 rounded-sm m-auto items-center">
            <FileIcon className=" text-primary-300 rounded-sm block text-md" size={16} />
          </span>
        )}
      </div>
      <div className="flex flex-col -mt-2">
        <h5 className="text-sm text-left">{title}</h5>
        {subTitle === 'default-workspace' ? (
          <p className="text-gray-600 text-xs flex items-center">{workspaceName}</p>
        ) : (
          <div className="flex flex-row">
            <p className="text-gray-400 text-xs flex items-center">Workspace:&nbsp;</p>
            <p className="text-gray-600 text-xs flex items-center">{workspaceName}</p>
          </div>
        )}
      </div>
    </button>
  );
};

export const FilePopup = ({
  onSubmit,
}: {
  onSubmit: (filId: string, fileTitle: string, fileIcon: any) => void;
}) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [searchString, setSearchString] = useState<string>('');
  const [searchItems, setSearchItems] = useState<SearchDataProps>({ total: 0, items: [] });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const firstListItemRef = useRef<HTMLButtonElement>(null);
  const limit = 10;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toggleMenu = (value?: boolean) => () => {
    setIsOpenMenu(value ?? !isOpenMenu);
    if (value) {
      setTimeout(() => {
        searchInputRef?.current?.focus();
      }, 100);
    }
  };
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const workspaceId = currentFile?.workspace?.id;

  const searchFiles = async (search: string, start = 0, clear = false) => {
    setIsLoading(true);
    const result = await API.get(
      `${endpoints.search.filesearchurl}?search=${search}&start=${start}&limit=${limit}`
    );

    setIsLoading(false);
    if (result.data) {
      const total = result.data.total;
      setSearchItems((prev) => {
        const newItems = result.data.items;

        const allItems = clear ? newItems : (prev?.items || []).concat(newItems);
        return { total, items: allItems };
      });
      return result;
    }

    return { total: 0, items: [] };
  };

  useEffect(() => {
    searchFiles(' ', 0, true);
  }, []);

  useEffect(() => {
    if (searchItems?.items?.length >= searchItems?.total) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
  }, [searchItems]);

  const onSelectFile = (id: string, title: string, icon?: any) => {
    setIsOpenMenu(false);
    onSubmit(id, title, icon);
  };

  const handleFetchMore = () => {
    const pageNum = currentPage + 1;
    setCurrentPage(pageNum);
    searchFiles(searchString || ' ', pageNum);
  };

  useEffect(() => {
    setCurrentPage(0);
    (searchString.length > 1 || searchString === '') && searchFiles(searchString || ' ', 0, true);
  }, [searchString]);

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpenMenu}
      onOpen={toggleMenu(true)}
      onClose={toggleMenu(false)}
      modifiers={[{ name: 'eventListeners', options: { scroll: isOpenMenu } }]}
    >
      <PopoverTrigger>
        <a className="underline text-primary-600 hover:text-primary-400 transition-all">
          Select a file...
        </a>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <div className="flex flex-col p-16 rounded-lg bg-white shadow-sm text-gray-999 w-400">
            <SearchInput
              setSearchString={setSearchString}
              inputRef={searchInputRef}
              nextRef={firstListItemRef}
            />
            <div
              id="scrollable"
              className="max-h-200 overflow-auto search-dialog-result-body relative mb-8"
            >
              {isLoading && (
                <div className="opacity-70 absolute top-0 left-0 w-full h-200 flex items-center justify-center bg-white">
                  <LoaderXs />
                </div>
              )}
              <InfiniteScroll
                dataLength={searchItems?.items?.length || 0}
                next={handleFetchMore}
                hasMore={hasMore}
                loader={
                  <div className="h-50 overflow-hidden">
                    <LoaderXs className="mx-auto text-xss" />
                  </div>
                }
                scrollableTarget="scrollable"
                endMessage={<hr className="border-dotted mt-8" />}
              >
                {searchItems.items?.length > 0 ? (
                  searchItems.items.map((item, index) => (
                    <SearchItem
                      key={`search-${item.id}`}
                      id={item.id}
                      title={item.title}
                      subTitle={item.workspace}
                      isShared={item.isShared}
                      icon={router.query.mode === 'a' ? searchIcons[item.icon] : item.icon}
                      onSelect={onSelectFile}
                      itemRef={index === 0 ? firstListItemRef : null}
                    />
                  ))
                ) : (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center ">
                      {searchString ? (
                        <>
                          <p className="text-gray-600 font-medium mb-8">No result found</p>
                          <p>Try again using different search terms</p>
                        </>
                      ) : (
                        <p className="text-gray-600 font-medium mb-8 animate-pulse">Loading...</p>
                      )}
                    </div>
                  </div>
                )}
              </InfiniteScroll>
            </div>
            <div>
              <button
                onClick={onOpen}
                className="flex w-full py-6 px-8 text-white text-center justify-center text-xs search-dialog-result-item cursor-pointer bg-primary-400 rounded-sm"
              >
                <AddIcon className="text-xs mt-4 mr-8" />
                Create a New File
              </button>
              <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent style={{ maxWidth: '750px', borderRadius: '8px' }}>
                  <ModalCloseButton />
                  <StartNewFile
                    fileWorkspace={workspaceId}
                    onClose={onClose}
                    requireFileName={true}
                    onCreateFile={onSelectFile}
                    redirectToFile={false}
                  />
                </ModalContent>
              </Modal>
            </div>
          </div>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
